import { useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { Icon } from "@/components/ui/Icon";
import { PageHeader } from "@/components/ui/PageHeader";
import { useAuth } from "@/hooks/useAuth";
import { useSpace } from "@/hooks/useSpace";
import { adminScopeOf } from "@/domain/space";
import { useWorkdayConfig } from "@/hooks/useWorkdayConfig";
import { usePersonalEvents } from "@/hooks/usePersonalEvents";
import { useJoongangSchedule } from "@/hooks/useJoongangSchedule";
import { useAsconProdDays } from "@/hooks/useAsconProdDays";
import { findMenu, menuKeyOfPath } from "@/domain/menus";
import { kstToday } from "@/domain/rqc";
import { nonWorkdayReason } from "@/domain/workday";
import { nextWorkdays } from "@/domain/mySchedule";
import { AllMenus } from "./AllMenus";
import { AdminPanel } from "./AdminPanel";
import { PreviewNote } from "./PreviewNote";
import { TodayEventsCard, UpcomingCard } from "./ScheduleCards";
import { GyeongwonTodayCard, JoongangTodayCard } from "./TodayProduction";
import { useEventEditor } from "./useEventEditor";

/**
 * 내 업무 공간 (B′ 미리보기, 2026-10-19 · 2차 반영).
 *  - MySpaceHome   : /me — 순서(정보 최소화)
 *        (관리자만) 관리 블록 — 처리 대기 숫자 + 링크
 *        ① 오늘      — 법인별 금일 출하/생산 카드(대시보드 카드 재사용) + 오늘 개인 일정
 *        ② 다가오는 일정(+3 / +7 영업일) + 할 일 추가
 *  - MySpaceAll    : /my/all  — 전체 보기
 *  - SpacePlaceholder : 자리만 잡은 메뉴
 *  - 일정·할 일(/my/tasks) 은 Tasks.tsx
 *
 * 데이터: 중앙 생산 = 실데이터(오늘: RQC 라이브 / 이후: Firestore 예정),
 *         경원 생산 = OKIS 미러(오늘: 실시간 구독 / 이후: 수주량, 2026-10-22),
 *         개인 일정·할 일 = users/{uid}/personal_events (rules 배포 전에는 로컬 저장 대체).
 * 전체 관리자는 소속과 무관하게 두 법인 오늘 카드를 나란히 본다(D-13 "경영은 금일 아스콘·레미콘 일정을 같이").
 * 법인 관리자(중앙/경원 관리자로 등록된 사람, D-08)는 그 법인 관리 칸만 본다(adminScopeOf).
 */

/** 다가오는 일정 최대 범위(영업일). 카드 세그먼트가 +3/+7 로 자른다 */
const UPCOMING_WORKDAYS = 7;

function plusOneDay(ymd: string): string {
  const [y, m, d] = ymd.split("-").map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + 1));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, "0")}-${String(dt.getUTCDate()).padStart(2, "0")}`;
}

export function MySpaceHome() {
  const { membership, tenants } = useSpace();
  const { user, hasPerm } = useAuth();
  // 관리 범위: 전체 관리자(두 법인 + 그룹 항목) / 법인 관리자(그 법인만, D-08)
  const scope = adminScopeOf(membership, !!user?.isAdmin);
  const { config, loaded: wdLoaded } = useWorkdayConfig();

  const today = kstToday();
  const offReason = wdLoaded ? nonWorkdayReason(today, config) : null;

  // 출하조회 권한(중앙 daily_shipment·daily_shipments, 경원 ascon_* read 게이트에 모두 포함) + (소속 또는 관리자)
  const shipPerm = hasPerm("shipment.view") || hasPerm("dispatch.view");
  const showJoongang = shipPerm && (tenants.includes("joongang") || scope.tenants.includes("joongang"));
  const showGyeongwon = shipPerm && (tenants.includes("gyeongwon") || scope.tenants.includes("gyeongwon"));

  const nextDates = useMemo(
    () => (wdLoaded ? nextWorkdays(today, UPCOMING_WORKDAYS, config) : []),
    [wdLoaded, today, config],
  );
  const jSched = useJoongangSchedule(showJoongang && wdLoaded, nextDates);
  const jLoading = showJoongang && (!wdLoaded || jSched.loading);

  // 경원 다가오는 일정 = 내일 ~ 마지막 영업일 수주(아스콘은 띄엄띄엄 — 영업일 아닌 날 수주도 그대로 보인다)
  const gSched = useAsconProdDays(showGyeongwon && nextDates.length > 0, plusOneDay(today), nextDates[nextDates.length - 1] ?? "");

  const pe = usePersonalEvents();
  const events = pe.events.filter((e) => e.kind === "event");
  const todayEvents = events.filter((e) => e.date === today);
  const laterEvents = events.filter((e) => e.date > today);

  const holidayLabel = useCallback((ymd: string) => nonWorkdayReason(ymd, config), [config]);
  const editor = useEventEditor(pe, today, holidayLabel);

  const prodCards = (showJoongang ? 1 : 0) + (showGyeongwon ? 1 : 0);

  return (
    <div className="page my-space">
      <PreviewNote />
      <PageHeader title="내 업무" desc="오늘과 다가오는 일정을 모아 봅니다. 고치는 일은 각 법인 공간에서 합니다." />

      {(scope.group || scope.tenants.length > 0) && <AdminPanel scope={scope} />}

      <div className={`my-today-grid is-n${prodCards}`}>
        {showJoongang && <JoongangTodayCard />}
        {showGyeongwon && <GyeongwonTodayCard />}
        <TodayEventsCard
          today={today}
          offReason={offReason}
          events={todayEvents}
          eventsMode={pe.mode}
          onAdd={() => editor.openNew(today, "event")}
          onOpen={editor.openEdit}
        />
      </div>

      <div className="my-col">
        <UpcomingCard
          showJoongang={showJoongang}
          workdays={nextDates}
          joongangDays={jSched.days}
          joongangLoading={jLoading}
          joongangError={jSched.error}
          showGyeongwon={showGyeongwon}
          gyeongwonDays={gSched.days}
          gyeongwonLoading={showGyeongwon && (!wdLoaded || gSched.loading)}
          gyeongwonError={gSched.error}
          events={laterEvents}
          onAddTodo={() => editor.openNew(today, "todo")}
          onOpen={editor.openEdit}
        />

      </div>

      {editor.modal}
    </div>
  );
}

export function MySpaceAll() {
  return (
    <div className="page my-space">
      <PreviewNote />
      <PageHeader title="전체 보기" desc="근무 법인의 메뉴를 한 장에 모았습니다. 링크를 누르면 그 법인 공간으로 이동합니다." />
      <AllMenus />
    </div>
  );
}

export function SpacePlaceholder() {
  const { pathname } = useLocation();
  const menu = findMenu(menuKeyOfPath(pathname));
  return (
    <div className="page">
      <PageHeader title={menu?.label ?? "준비 중"} desc="B′ 미리보기에서 메뉴 자리만 잡아 두었습니다." />
      <div className="card empty-state">
        <Icon name="cube" size={40} />
        <div className="my-empty-text">준비 중인 메뉴입니다</div>
      </div>
    </div>
  );
}
